import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'

type TFn = (key: string) => string

function friendlyError(err: unknown, t: TFn): string {
  const code = (err as { code?: string })?.code || ''
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return t('auth.errors.invalidCredential')
    case 'auth/invalid-email':
      return t('auth.errors.invalidEmail')
    case 'auth/too-many-requests':
      return t('auth.errors.tooManyRequests')
    case 'auth/network-request-failed':
      return t('auth.errors.networkRequestFailed')
    case 'auth/account-exists-with-different-credential':
      return t('webapp.authErrors.accountExists')
    case 'auth/popup-blocked':
      return t('webapp.authErrors.popupBlocked')
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return ''
    default:
      return t('auth.errors.generic')
  }
}

export function SignIn() {
  const { t } = useTranslation()
  const { signInEmail, signInGoogle, signInApple, resetPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await fn()
    } catch (err) {
      const msg = friendlyError(err, t)
      if (msg) setError(msg)
    } finally {
      setBusy(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void run(() => signInEmail(email.trim(), password))
  }

  async function onForgot() {
    if (!email.trim()) {
      setError(t('auth.enterEmailForReset'))
      return
    }
    await run(async () => {
      await resetPassword(email.trim())
      setNotice(t('auth.checkEmailForReset'))
    })
  }

  const input = 'rounded-lg border border-border bg-bg-elevated px-md py-smd text-body text-text outline-none focus:border-primary'

  return (
    <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
      <div className="w-full max-w-md animate-fade-in rounded-xxl border border-border-light bg-surface p-xl shadow-lg">
        <div className="mb-lg flex flex-col items-center gap-sm">
          <img src="/icon-192.png" alt="" className="h-16 w-16 rounded-[18px]" />
          <h1 className="text-h2 font-bold text-text">{t('app.name')}</h1>
          <p className="text-center text-body-sm text-text-secondary">{t('webapp.authSubtitle')}</p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-md">
          <label className="flex flex-col gap-xs">
            <span className="text-body-sm font-medium text-text-secondary">{t('auth.email')}</span>
            <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} />
          </label>
          <label className="flex flex-col gap-xs">
            <span className="text-body-sm font-medium text-text-secondary">{t('auth.password')}</span>
            <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
          </label>

          {error ? <p className="text-body-sm text-risk-high">{error}</p> : null}
          {notice ? <p className="text-body-sm text-risk-safe">{notice}</p> : null}

          <button type="submit" disabled={busy} className="mt-xs rounded-lg bg-primary px-md py-smd text-button font-semibold text-on-primary transition-opacity hover:opacity-90 disabled:opacity-60">
            {t('auth.logIn')}
          </button>
          <button type="button" onClick={() => void onForgot()} disabled={busy} className="text-body-sm text-primary hover:underline">
            {t('auth.forgotPassword')}
          </button>
        </form>

        <div className="my-lg flex items-center gap-md">
          <span className="h-px flex-1 bg-border" />
          <span className="text-caption text-text-muted">{t('authSheet.and')}</span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="flex flex-col gap-sm">
          <button onClick={() => void run(signInGoogle)} disabled={busy} className="rounded-lg border border-border bg-bg-elevated px-md py-smd text-button font-medium text-text hover:bg-surface-inner disabled:opacity-60">
            {t('authSheet.continueWithGoogle')}
          </button>
          <button onClick={() => void run(signInApple)} disabled={busy} className="rounded-lg border border-border bg-bg-elevated px-md py-smd text-button font-medium text-text hover:bg-surface-inner disabled:opacity-60">
            {t('authSheet.continueWithApple')}
          </button>
        </div>

        <p className="mt-lg text-center text-caption text-text-muted">{t('webapp.authNoSignup')}</p>
      </div>
    </div>
  )
}
